import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Modal, Form, Spinner, Dropdown, Tabs, Tab, ButtonGroup } from 'react-bootstrap';
import { Mail, Eye, Trash2, Filter, CheckCircle, Circle, XCircle, Users, Grid, List } from 'lucide-react';
import { showError, showSuccess } from '../../../utils/notifications';
import contactRequestService, { ContactRequest } from '../../../services/contactRequestService';
import contactLeadService, { ContactLead } from '../../../services/contactLeadService';
import { useUserData } from '../../../hooks';
import ContactRequestsListView from '../../../components/contact-requests/ContactRequestsListView';
import ContactLeadsListView from '../../../components/contact-requests/ContactLeadsListView';
import { ERROR_MESSAGES, handleError } from '../../../utils/errorHandler';

type ViewMode = 'table' | 'list';

const ContactRequests = () => {
    const { canEdit } = useUserData();

    // Contact Requests state
    const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('');

    // Contact Leads state
    const [contactLeads, setContactLeads] = useState<ContactLead[]>([]);
    const [selectedLead, setSelectedLead] = useState<ContactLead | null>(null);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [filterLeadStatus, setFilterLeadStatus] = useState<string>('');

    // Loading state
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<string>('requests');
    const [isMobile, setIsMobile] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    // Detect mobile and force list view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Force list view on mobile/tablet
            if (mobile) {
                setViewMode('list');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (activeTab === 'requests') {
            fetchContactRequests();
        } else {
            fetchContactLeads();
        }
    }, [filterStatus, filterLeadStatus, activeTab]);

    const fetchContactRequests = async () => {
        try {
            setLoading(true);
            const data = await contactRequestService.getAll(filterStatus || undefined);
            setContactRequests(data);
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    };

    const fetchContactLeads = async () => {
        try {
            setLoading(true);
            const data = await contactLeadService.getAll(filterLeadStatus || undefined);
            setContactLeads(data);
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    };

    const handleView = (request: ContactRequest) => {
        setSelectedRequest(request);
        setShowViewModal(true);

        // Mark as read if status is 'new'
        if (request.status === 'new') {
            handleUpdateStatus(request.id, 'read', false);
        }
    };

    const handleUpdateStatus = async (id: number, status: 'new' | 'read' | 'closed', showNotification = true) => {
        try {
            await contactRequestService.updateStatus(id, status);
            if (showNotification) {
                showSuccess('Success', 'Status updated successfully');
                setShowViewModal(false); // Close modal after status update
            }
            fetchContactRequests();
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_UPDATE_FAILED));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this contact request?')) {
            return;
        }

        try {
            await contactRequestService.delete(id);
            showSuccess('Success', 'Contact request deleted successfully');
            fetchContactRequests();
            setShowViewModal(false);
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_DELETE_FAILED));
        }
    };

    // Contact Lead handlers
    const handleViewLead = (lead: ContactLead) => {
        setSelectedLead(lead);
        setShowLeadModal(true);

        // Mark as read if status is 'new'
        if (lead.status === 'new') {
            handleUpdateLeadStatus(lead.id, 'read', false);
        }
    };

    const handleUpdateLeadStatus = async (id: number, status: 'new' | 'read' | 'closed', showNotification = true) => {
        try {
            await contactLeadService.updateStatus(id, status);
            if (showNotification) {
                showSuccess('Success', 'Status updated successfully');
                setShowLeadModal(false); // Close modal after status update
            }
            fetchContactLeads();
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_UPDATE_FAILED));
        }
    };

    const handleDeleteLead = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this contact lead?')) {
            return;
        }

        try {
            await contactLeadService.delete(id);
            showSuccess('Success', 'Contact lead deleted successfully');
            fetchContactLeads();
            setShowLeadModal(false);
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.CONTACT_REQUESTS_DELETE_FAILED));
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <Badge bg="primary"><Circle size={12} className="me-1" />New</Badge>;
            case 'read':
                return <Badge bg="info"><Eye size={12} className="me-1" />Read</Badge>;
            case 'closed':
                return <Badge bg="success"><CheckCircle size={12} className="me-1" />Closed</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getWhatsAppLink = (phoneNumber: string) => {
        // Remove all non-numeric characters except +
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    if (loading) {
        return (
            <div>
                <h4 className="mb-4 d-none d-lg-block">Contacts Management</h4>
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading...</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && <h4 className="mb-0">Contact Requests</h4>}
                <div className={`d-flex gap-2 ${isMobile ? 'w-100 flex-wrap' : ''}`}>
                    {/* View Toggle */}
                    <ButtonGroup className={isMobile ? 'flex-grow-1' : ''}>
                        <Button
                            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('table')}
                            title={isMobile ? "Table view not available on mobile" : "Table View"}
                            className={isMobile ? 'flex-grow-1' : ''}
                            disabled={isMobile}
                            style={{
                                opacity: isMobile ? 0.6 : 1,
                                cursor: isMobile ? 'not-allowed' : 'pointer',
                                backgroundColor: isMobile ? '#e9ecef' : undefined,
                                borderColor: isMobile ? '#dee2e6' : undefined,
                                color: isMobile ? '#6c757d' : undefined
                            }}
                        >
                            <Grid size={18} />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('list')}
                            title="Card View"
                            className={isMobile ? 'flex-grow-1' : ''}
                        >
                            <List size={18} />
                        </Button>
                    </ButtonGroup>

                    {/* Filter Dropdown */}
                    {activeTab === 'requests' ? (
                        <Dropdown className={isMobile ? 'w-100' : ''}>
                            <Dropdown.Toggle variant="outline-primary" id="dropdown-filter" className={isMobile ? 'w-100' : ''}>
                                <Filter size={18} className="me-2" />
                                {isMobile ? 'Filter' : `Filter: ${filterStatus || 'All'}`}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setFilterStatus('')}>All</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterStatus('new')}>New</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterStatus('read')}>Read</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterStatus('closed')}>Closed</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        <Dropdown className={isMobile ? 'w-100' : ''}>
                            <Dropdown.Toggle variant="outline-primary" id="dropdown-filter-leads" className={isMobile ? 'w-100' : ''}>
                                <Filter size={18} className="me-2" />
                                {isMobile ? 'Filter' : `Filter: ${filterLeadStatus || 'All'}`}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setFilterLeadStatus('')}>All</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterLeadStatus('new')}>New</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterLeadStatus('read')}>Read</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilterLeadStatus('closed')}>Closed</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k || 'requests')}
                className="mb-3"
            >
                <Tab eventKey="requests" title={
                    <span><Mail size={16} className="me-2" />Contact Forms ({contactRequests.length})</span>
                }>
                    {/* Contact Requests */}
                    {contactRequests.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Mail size={48} className="text-muted mb-3" />
                        <p className="text-muted">No contact requests found</p>
                    </Card.Body>
                </Card>
            ) : viewMode === 'list' ? (
                <ContactRequestsListView
                    requests={contactRequests}
                    canEdit={canEdit}
                    onView={handleView}
                    onDelete={handleDelete}
                />
            ) : (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th>Date</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Subject</th>
                                    <th>Status</th>
                                    <th>Source</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contactRequests.map((request) => (
                                    <tr key={request.id} style={{ cursor: 'pointer' }}>
                                        <td onClick={() => handleView(request)}>
                                            <small>{formatDate(request.created_at)}</small>
                                        </td>
                                        <td onClick={() => handleView(request)}>
                                            {request.name} {request.last_name || ''}
                                        </td>
                                        <td onClick={() => handleView(request)}>
                                            <a href={`mailto:${request.email}`} onClick={(e) => e.stopPropagation()}>
                                                {request.email}
                                            </a>
                                        </td>
                                        <td onClick={() => handleView(request)}>
                                            {request.indicative && request.number ? (
                                                <a href={getWhatsAppLink(`${request.indicative}${request.number}`)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                    {request.indicative} {request.number}
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td onClick={() => handleView(request)}>{request.subject || '-'}</td>
                                        <td onClick={() => handleView(request)}>{getStatusBadge(request.status)}</td>
                                        <td onClick={() => handleView(request)}>
                                            <Badge bg="secondary">{request.source || 'unknown'}</Badge>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleView(request)}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                            {canEdit && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(request.id);
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
                </Tab>

                <Tab eventKey="leads" title={
                    <span><Users size={16} className="me-2" />Quick Leads ({contactLeads.length})</span>
                }>
                    {/* Contact Leads */}
                    {contactLeads.length === 0 ? (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="text-center py-5">
                                <Users size={48} className="text-muted mb-3" />
                                <p className="text-muted">No contact leads found</p>
                            </Card.Body>
                        </Card>
                    ) : viewMode === 'list' ? (
                        <ContactLeadsListView
                            leads={contactLeads}
                            canEdit={canEdit}
                            onView={handleViewLead}
                            onDelete={handleDeleteLead}
                        />
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-0">
                                <Table responsive hover className="mb-0">
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                        <tr>
                                            <th>Date</th>
                                            <th>Contact</th>
                                            <th>Status</th>
                                            <th>Source</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contactLeads.map((lead) => (
                                            <tr key={lead.id} style={{ cursor: 'pointer' }}>
                                                <td onClick={() => handleViewLead(lead)}>
                                                    <small>{formatDate(lead.created_at)}</small>
                                                </td>
                                                <td onClick={() => handleViewLead(lead)}>
                                                    {lead.contact.includes('@') ? (
                                                        <a href={`mailto:${lead.contact}`} onClick={(e) => e.stopPropagation()}>
                                                            {lead.contact}
                                                        </a>
                                                    ) : (
                                                        <a href={getWhatsAppLink(lead.contact)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                            {lead.contact}
                                                        </a>
                                                    )}
                                                </td>
                                                <td onClick={() => handleViewLead(lead)}>{getStatusBadge(lead.status)}</td>
                                                <td onClick={() => handleViewLead(lead)}>
                                                    <Badge bg="secondary">{lead.source || 'unknown'}</Badge>
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={() => handleViewLead(lead)}
                                                    >
                                                        <Eye size={16} />
                                                    </Button>
                                                    {canEdit && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteLead(lead.id);
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                </Tab>
            </Tabs>

            {/* View Modal for Contact Requests */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Contact Request Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRequest && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Name:</strong><br />
                                    {selectedRequest.name} {selectedRequest.last_name || ''}
                                </div>
                                <div className="col-md-6">
                                    <strong>Email:</strong><br />
                                    <a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Phone:</strong><br />
                                    {selectedRequest.indicative && selectedRequest.number ? (
                                        <a href={getWhatsAppLink(`${selectedRequest.indicative}${selectedRequest.number}`)} target="_blank" rel="noopener noreferrer">
                                            {selectedRequest.indicative} {selectedRequest.number}
                                        </a>
                                    ) : '-'}
                                </div>
                                <div className="col-md-6">
                                    <strong>Subject:</strong><br />
                                    {selectedRequest.subject || '-'}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Status:</strong><br />
                                    {getStatusBadge(selectedRequest.status)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Source:</strong><br />
                                    <Badge bg="secondary">{selectedRequest.source || 'unknown'}</Badge>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Date:</strong><br />
                                    {formatDate(selectedRequest.created_at)}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Message:</strong><br />
                                    <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedRequest.message}
                                    </div>
                                </div>
                            </div>

                            {canEdit && (
                                <div className="row">
                                    <div className="col-12">
                                        <strong>Update Status:</strong><br />
                                        <div className="btn-group mt-2" role="group">
                                            <Button
                                                variant={selectedRequest.status === 'new' ? 'primary' : 'outline-primary'}
                                                size="sm"
                                                onClick={() => handleUpdateStatus(selectedRequest.id, 'new')}
                                            >
                                                New
                                            </Button>
                                            <Button
                                                variant={selectedRequest.status === 'read' ? 'info' : 'outline-info'}
                                                size="sm"
                                                onClick={() => handleUpdateStatus(selectedRequest.id, 'read')}
                                            >
                                                Read
                                            </Button>
                                            <Button
                                                variant={selectedRequest.status === 'closed' ? 'success' : 'outline-success'}
                                                size="sm"
                                                onClick={() => handleUpdateStatus(selectedRequest.id, 'closed')}
                                            >
                                                Closed
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {canEdit && selectedRequest && (
                        <Button variant="danger" onClick={() => handleDelete(selectedRequest.id)}>
                            <Trash2 size={16} className="me-2" />
                            Delete
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Modal for Contact Leads */}
            <Modal show={showLeadModal} onHide={() => setShowLeadModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Contact Lead Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLead && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Contact:</strong><br />
                                    {selectedLead.contact.includes('@') ? (
                                        <a href={`mailto:${selectedLead.contact}`}>{selectedLead.contact}</a>
                                    ) : (
                                        <a href={getWhatsAppLink(selectedLead.contact)} target="_blank" rel="noopener noreferrer">{selectedLead.contact}</a>
                                    )}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Status:</strong><br />
                                    {getStatusBadge(selectedLead.status)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Source:</strong><br />
                                    <Badge bg="secondary">{selectedLead.source || 'unknown'}</Badge>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Date:</strong><br />
                                    {formatDate(selectedLead.created_at)}
                                </div>
                            </div>

                            {canEdit && (
                                <div className="row">
                                    <div className="col-12">
                                        <strong>Update Status:</strong><br />
                                        <div className="btn-group mt-2" role="group">
                                            <Button
                                                variant={selectedLead.status === 'new' ? 'primary' : 'outline-primary'}
                                                size="sm"
                                                onClick={() => handleUpdateLeadStatus(selectedLead.id, 'new')}
                                            >
                                                New
                                            </Button>
                                            <Button
                                                variant={selectedLead.status === 'read' ? 'info' : 'outline-info'}
                                                size="sm"
                                                onClick={() => handleUpdateLeadStatus(selectedLead.id, 'read')}
                                            >
                                                Read
                                            </Button>
                                            <Button
                                                variant={selectedLead.status === 'closed' ? 'success' : 'outline-success'}
                                                size="sm"
                                                onClick={() => handleUpdateLeadStatus(selectedLead.id, 'closed')}
                                            >
                                                Closed
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {canEdit && selectedLead && (
                        <Button variant="danger" onClick={() => handleDeleteLead(selectedLead.id)}>
                            <Trash2 size={16} className="me-2" />
                            Delete
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowLeadModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ContactRequests;
